import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Pencil, Plus, Power, PowerOff } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarOrganismo,
  crearOrganismo,
  listarOrganismos,
  type OrganismoCreatePayload,
  type OrganismoRead,
  type OrganismoUpdatePayload,
} from "./api";

export function OrganismosPage(): JSX.Element {
  const [editing, setEditing] = useState<OrganismoRead | "new" | null>(null);
  const [incluirInactivos, setIncluirInactivos] = useState(false);

  const organismosQ = useQuery({
    queryKey: ["organismos", incluirInactivos],
    queryFn: () => listarOrganismos(incluirInactivos),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Organismos"
        icon={Building2}
        subtitle={
          <>
            Catálogo de instituciones que pagan subsidios al consumo (FNDR,
            Municipalidad, etc.). Cada decreto de subsidio se asocia a un
            organismo, y la rendición mensual se filtra por el organismo
            destinatario.
            {organismosQ.data && <> ({organismosQ.data.length} organismos)</>}
          </>
        }
        actions={
          <>
            <label className="inline-flex items-center gap-1 text-xs text-neutral-600">
              <input
                type="checkbox"
                checked={incluirInactivos}
                onChange={(e) => setIncluirInactivos(e.target.checked)}
                className="rounded border-neutral-450 text-brand-600 focus:ring-brand-500"
              />
              Incluir inactivos
            </label>
            <Button icon={Plus} onClick={() => setEditing("new")}>
              Nuevo organismo
            </Button>
          </>
        }
      />

      {editing && (
        <OrganismoForm
          organismo={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {organismosQ.isPending && <Cargando />}
      {organismosQ.isError && (
        <ErrorCarga
          error={organismosQ.error}
          mensaje="Error al cargar organismos."
          onReintentar={() => void organismosQ.refetch()}
        />
      )}

      {organismosQ.data && organismosQ.data.length === 0 && (
        <EmptyState
          icon={Building2}
          title="Aún no hay organismos cargados"
          description="Los organismos subsidiadores son las entidades que pagan los subsidios al comité (FNDR Región, municipalidad, etc.). Carga el primero para poder rendir."
        >
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Crear primer organismo
          </Button>
        </EmptyState>
      )}

      {organismosQ.data && organismosQ.data.length > 0 && (
        <TableWrap label="Organismos subsidiadores">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Código</th>
                <th scope="col" className="px-3 py-2 text-left">Nombre</th>
                <th scope="col" className="px-3 py-2 text-left">RUT</th>
                <th scope="col" className="px-3 py-2 text-left">Contacto</th>
                <th scope="col" className="px-3 py-2 text-left">Email</th>
                <th scope="col" className="px-3 py-2 text-center">Activo</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {organismosQ.data.map((o) => (
                <OrganismoRow
                  key={o.id}
                  organismo={o}
                  onEdit={() => setEditing(o)}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function OrganismoRow({
  organismo,
  onEdit,
}: {
  organismo: OrganismoRead;
  onEdit: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const toggle = useMutation({
    mutationFn: () =>
      actualizarOrganismo(organismo.id, { activo: !organismo.activo }),
    onSuccess: (o) => {
      toast.success(`"${o.nombre}" ${o.activo ? "activado" : "desactivado"}`);
      void qc.invalidateQueries({ queryKey: ["organismos"] });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo actualizar";
      toast.error(msg);
    },
  });

  return (
    <tr
      className={
        organismo.activo
          ? "hover:bg-neutral-50"
          : "bg-neutral-50/60 text-neutral-500"
      }
    >
      <td className="px-3 py-2 font-mono text-xs text-neutral-700">
        {organismo.codigo}
      </td>
      <td className="px-3 py-2 font-medium text-neutral-900">
        {organismo.nombre}
        {organismo.observaciones && (
          <div className="text-xs text-neutral-500 font-normal max-w-xs truncate">
            {organismo.observaciones}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-neutral-600">{organismo.rut ?? "—"}</td>
      <td className="px-3 py-2 text-neutral-600">{organismo.contacto ?? "—"}</td>
      <td className="px-3 py-2 text-neutral-600">{organismo.email ?? "—"}</td>
      <td className="px-3 py-2 text-center">
        {organismo.activo ? (
          <Badge tone="emerald">
            <Power className="h-3 w-3" strokeWidth={1.5} />
            Activo
          </Badge>
        ) : (
          <Badge tone="slate">
            <PowerOff className="h-3 w-3" strokeWidth={1.5} />
            Inactivo
          </Badge>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-3">
          <Button variant="link" size="sm" icon={Pencil} onClick={onEdit}>
            Editar
          </Button>
          <Button
            variant="link"
            size="sm"
            icon={organismo.activo ? PowerOff : Power}
            onClick={() => toggle.mutate()}
            disabled={toggle.isPending}
          >
            {organismo.activo ? "Desactivar" : "Activar"}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function OrganismoForm({
  organismo,
  onClose,
}: {
  organismo: OrganismoRead | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = organismo !== null;

  const [codigo, setCodigo] = useState(organismo?.codigo ?? "");
  const [nombre, setNombre] = useState(organismo?.nombre ?? "");
  const [rut, setRut] = useState(organismo?.rut ?? "");
  const [email, setEmail] = useState(organismo?.email ?? "");
  const [contacto, setContacto] = useState(organismo?.contacto ?? "");
  const [observaciones, setObservaciones] = useState(
    organismo?.observaciones ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  const crearMut = useMutation({
    mutationFn: (p: OrganismoCreatePayload) => crearOrganismo(p),
    onSuccess: (o) => {
      toast.success(`Organismo "${o.nombre}" creado`);
      void qc.invalidateQueries({ queryKey: ["organismos"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: OrganismoUpdatePayload) =>
      actualizarOrganismo((organismo as OrganismoRead).id, p),
    onSuccess: (o) => {
      toast.success(`Organismo "${o.nombre}" actualizado`);
      void qc.invalidateQueries({ queryKey: ["organismos"] });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;

  function submit(): void {
    setError(null);
    if (!nombre.trim()) return setError("Nombre requerido");
    if (!editando && !codigo.trim())
      return setError("Código requerido (ej: FNDR-XV)");

    const payload = {
      nombre: nombre.trim(),
      rut: rut.trim() || null,
      email: email.trim() || null,
      contacto: contacto.trim() || null,
      observaciones: observaciones.trim() || null,
    };

    if (editando) {
      actualizarMut.mutate(payload);
    } else {
      crearMut.mutate({ codigo: codigo.trim(), ...payload });
    }
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([codigo, nombre, rut, email, contacto, observaciones]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crearMut.isPending || actualizarMut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={editando ? "Editar organismo" : "Nuevo organismo"}
      subtitle={editando && organismo ? organismo.codigo : undefined}
      icon={Building2}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending ? "Guardando…" : editando ? "Guardar cambios" : "Crear"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="Código (único, no editable)"
          required
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.toUpperCase())}
          disabled={editando}
          placeholder="FNDR-XV / MUNI-PUERTO-VARAS"
        />
        <Input
          label="Nombre"
          required
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />
        <Input
          label="RUT"
          value={rut}
          onChange={(e) => setRut(e.target.value)}
          placeholder="61.000.000-0"
        />
        <Input
          label="Contacto"
          value={contacto}
          onChange={(e) => setContacto(e.target.value)}
          placeholder="Nombre del referente"
        />
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Textarea
          label="Observaciones"
          containerClassName="sm:col-span-2"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
        />
      </div>

      {error && <p className="mt-3 text-xs text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}
